import React, { useState, useEffect } from 'react'
import {
  Modal,
  Tabs,
  Form,
  Input,
  Select,
  Radio,
  Divider,
  Row,
  Col,
  Checkbox,
  Space,
  Upload,
  Button,
  message,
  Alert
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { ApiRequest } from '../../../services/api/api'
import { baseUrl } from '../../../services/api/base-url'
import { getAuthHeaders } from '../../../services/lib/utils/auth.utils'
import { imgStringToArray, imgArrayToString } from '../../../components/common/common-column-components/common-edit-image-component'
import PackagingTypeCascadeSelect from '../../../components/common/packaging-type-cascade-select'
import SweetAlertService from '../../../services/lib/utils/sweet-alert-service'
import ProductPriceManagementPage from './product-price-management-page/product-price-management-page'
import MachineActionManagementPage from './machine-action-management-page/machine-action-management-page'
import '../product-management-dialog/compact-dialog-styles.css'

const { Option } = Select

interface BaseProductManagementDialogProps {
  visible: boolean
  onClose: (saved?: boolean, response?: any) => void
  baseProductData?: any
  mode?: 'add' | 'edit'
  onDataChanged?: () => void
}

const SPEC_NUMBER_FIELDS = [
  'brimFillCapacityMl', 'dimsTopLMm', 'dimsTopWMm', 'dimsBaseLMm', 'dimsBaseWMm',
  'productHeightMm', 'productWeightG', 'wallThicknessMm'
]

export const BaseProductManagementDialog: React.FC<BaseProductManagementDialogProps> = ({
  visible,
  onClose,
  baseProductData,
  mode = 'edit',
  onDataChanged
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState<any>(null)
  const [imgUrl, setImgUrl] = useState('')
  const [activeTab, setActiveTab] = useState('general')
  const [modeLocal, setModeLocal] = useState<'add' | 'edit'>(mode)
  const [currentBaseProductId, setCurrentBaseProductId] = useState<number | undefined>(undefined)

  // Lookup options
  const [rawMaterialOptions, setRawMaterialOptions] = useState<any[]>([])
  const [classificationOptions, setClassificationOptions] = useState<any[]>([])
  const [classificationPrimaryId, setClassificationPrimaryId] = useState<number | undefined>(undefined)
  const [classificationChildId, setClassificationChildId] = useState<number | undefined>(undefined)
  const [classificationGrandchildId, setClassificationGrandchildId] = useState<number | undefined>(undefined)

  // Load lookups
  useEffect(() => {
    if (!visible) return
    ApiRequest({ url: 'RawMaterial/GetAllRawMaterial', method: 'get', isShowSpinner: false })
      .then(res => setRawMaterialOptions(res.data?.data || []))
      .catch(() => setRawMaterialOptions([]))
    ApiRequest({ url: 'ProductClassification/GetAll?includeDeleted=false', method: 'get', isShowSpinner: false })
      .then(res => setClassificationOptions(res.data?.data || []))
      .catch(() => setClassificationOptions([]))
  }, [visible])

  const loadDetails = async (baseProductId: number) => {
    setLoading(true)
    try {
      const response = await ApiRequest({
        url: `BaseProduct/GetBaseProductById?id=${baseProductId}&inclInactive=1`,
        method: 'get',
        isShowSpinner: false
      })
      if (response.data?.data) {
        const row = response.data.data
        setDetails(row)
        setImgUrl(row.imgUrl || '')
        form.setFieldsValue({
          baseProductName: row.baseProductName || '',
          productCode: row.productCode || '',
          classificationId: row.classificationId ?? undefined,
          manufactured: row.manufactured != null ? Number(row.manufactured) : 0,
          visible: row.visible != null ? Number(row.visible) : 0,
          rawMaterialId: row.rawMaterialId ?? undefined,
          rawMaterialId2: row.rawMaterialId2 ?? undefined,
          packagingTypeId: row.packagingTypeId ?? undefined,
          description: row.description || '',
          rawmaterialDesc: row.rawmaterialDesc || '',
          cartonLength: row.cartonLength ?? undefined,
          cartonWidth: row.cartonWidth ?? undefined,
          cartonHeight: row.cartonHeight ?? undefined,
          grossWeigh: row.grossWeigh ?? undefined,
          ...SPEC_NUMBER_FIELDS.reduce((acc: any, field) => {
            acc[field] = row[field] ?? undefined
            return acc
          }, {}),
          rawMaterialText: row.rawMaterialText || '',
          manufacturedLocation: row.manufacturedLocation || '',
          printingType: row.printingType || '',
          liningCoating: row.liningCoating || '',
          facilityQualitySystem: row.facilityQualitySystem || '',
          isHomeCompostable: Number(row.isHomeCompostable) === 1,
          isIndustrialCompostable: Number(row.isIndustrialCompostable) === 1,
          isRecyclable: Number(row.isRecyclable) === 1
        })
      }
    } catch (error) {
      console.error('Failed to load base product:', error)
      message.error('Failed to load base product details')
    } finally {
      setLoading(false)
    }
  }

  // Load base product data in edit mode / reset in add mode
  useEffect(() => {
    const loadData = async () => {
      if (!visible) return
      setActiveTab('general')
      setModeLocal(mode)
      if (mode === 'edit' && baseProductData?.baseProductId) {
        setCurrentBaseProductId(baseProductData.baseProductId)
        form.resetFields()
        setDetails(null)
        await loadDetails(baseProductData.baseProductId)
      } else if (mode === 'add') {
        setCurrentBaseProductId(undefined)
        form.resetFields()
        form.setFieldsValue({ manufactured: 0, visible: 1 })
        setDetails(null)
        setImgUrl('')
        setClassificationPrimaryId(undefined)
        setClassificationChildId(undefined)
        setClassificationGrandchildId(undefined)
      }
    }
    loadData()
  }, [visible, mode, baseProductData])

  // Derive classification cascade path from loaded data
  useEffect(() => {
    const classificationId = details?.classificationId
    const map: any = {}
    classificationOptions.forEach((c: any) => { map[c.classificationId] = c })
    const path: any[] = []
    let current = classificationId ? map[classificationId] : null
    while (current) {
      path.unshift(current)
      current = current.parentId ? map[current.parentId] : null
    }
    // Options not loaded yet: clear selects only, keep the loaded form value
    if (!classificationOptions.length) {
      setClassificationPrimaryId(undefined)
      setClassificationChildId(undefined)
      setClassificationGrandchildId(undefined)
      return
    }
    setClassificationPrimaryId(path[0]?.classificationId)
    setClassificationChildId(path[1]?.classificationId)
    setClassificationGrandchildId(path[2]?.classificationId)
    // Keep hidden form field in sync (deepest selected node; preserve value if missing from options)
    const finalId = path.length ? path[path.length - 1].classificationId : (classificationId ?? null)
    form.setFieldsValue({ classificationId: finalId })
  }, [details, classificationOptions])

  const classificationChildrenOf = (parentId: number | null | undefined) =>
    classificationOptions.filter((c: any) =>
      parentId == null ? !c.parentId : c.parentId === parentId
    )

  const handleClassificationSelect = (level: 'primary' | 'child' | 'grandchild', id?: number) => {
    let finalId = id ?? null
    if (level === 'primary') {
      setClassificationPrimaryId(id)
      setClassificationChildId(undefined)
      setClassificationGrandchildId(undefined)
    } else if (level === 'child') {
      setClassificationChildId(id)
      setClassificationGrandchildId(undefined)
      finalId = id ?? classificationPrimaryId ?? null
    } else {
      setClassificationGrandchildId(id)
      finalId = id ?? classificationChildId ?? null
    }
    form.setFieldsValue({ classificationId: finalId })
  }

  const uploadProps: UploadProps = {
    name: 'imageFile',
    action: baseUrl + 'Common/UploadImage',
    // antd Upload 用自带 XHR 发请求，不走 axios 拦截器，必须手动带 token，否则 401
    headers: getAuthHeaders(),
    listType: 'picture-card',
    fileList: imgStringToArray(imgUrl),
    onChange: (info: any) => {
      if (info.file.response) {
        setImgUrl(imgArrayToString(info.fileList.map((row: any, index: number) => ({
          uid: index + 1,
          name: row.name,
          url: row.url || row.response
        }))))
      }
    },
    onRemove: (file: any) => {
      if (!file.response && file.url) {
        setImgUrl(imgArrayToString(imgStringToArray(imgUrl).filter((row: any) => row.url !== file.url)))
      }
    }
  }

  const toNumberOrNull = (v: any) =>
    (v === '' || v == null || isNaN(Number(v))) ? null : Number(v)

  const handleOk = async () => {
    try {
      const values = await form.validateFields()

      if (values.productCode && values.productCode.length > 22) {
        const result = await SweetAlertService.warningConfirm(
          `Product Code exceeds 22 characters (${values.productCode.length} chars). Do you want to continue saving?`,
              'Warning'
        )
        if (result.dismissed) return
      }

      setLoading(true)
      const payload: any = {
        ...(modeLocal === 'edit' ? (details || {}) : {}),
        ...values,
        manufactured: values.manufactured ? 1 : 0,
        visible: values.visible ? 1 : 0,
        imgUrl,
        classificationId: values.classificationId ?? null,
        cartonLength: toNumberOrNull(values.cartonLength),
        cartonWidth: toNumberOrNull(values.cartonWidth),
        cartonHeight: toNumberOrNull(values.cartonHeight),
        grossWeigh: values.grossWeigh ?? null,
        ...SPEC_NUMBER_FIELDS.reduce((acc: any, field) => {
          acc[field] = toNumberOrNull(values[field])
          return acc
        }, {}),
        rawMaterialText: values.rawMaterialText ?? null,
        manufacturedLocation: values.manufacturedLocation ?? null,
        printingType: values.printingType ?? null,
        liningCoating: values.liningCoating ?? null,
        facilityQualitySystem: values.facilityQualitySystem ?? null,
        isHomeCompostable: values.isHomeCompostable ? 1 : 0,
        isIndustrialCompostable: values.isIndustrialCompostable ? 1 : 0,
        isRecyclable: values.isRecyclable ? 1 : 0
      }

      let response
      if (modeLocal === 'add') {
        response = await ApiRequest({
          url: 'BaseProduct/AddBaseProduct',
          method: 'post',
          data: payload,
          isShowSpinner: false
        })
        message.success('Base product added successfully')

        // Stay open: switch to edit mode with the new id and jump to the price tab
        const newId = response?.data?.data
        onDataChanged?.()
        if (newId) {
          setModeLocal('edit')
          setCurrentBaseProductId(newId)
          setActiveTab('price')
          await loadDetails(newId)
        } else {
          onClose(true)
        }
        return
      } else {
        payload.baseProductId = baseProductData?.baseProductId
        response = await ApiRequest({
          url: 'BaseProduct/UpdateBaseProduct',
          method: 'put',
          data: payload,
          isShowSpinner: false
        })
        message.success('Base product updated successfully')
        onDataChanged?.()
      }
      onClose(true)
    } catch (error: any) {
      console.error('Save failed:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save base product'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const sizePairItem = (label: string, lengthName: string, widthName: string) => (
    <Col span={4} key={lengthName}>
      <Form.Item label={label} style={{ marginBottom: 2 }}>
        <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
          <Form.Item name={lengthName} noStyle>
            <Input size="small" placeholder="L" style={{ width: 'calc(50% - 6px)' }} />
          </Form.Item>
          <span style={{ padding: '0 2px', fontSize: '12px', color: '#999' }}>x</span>
          <Form.Item name={widthName} noStyle>
            <Input size="small" placeholder="W" style={{ width: 'calc(50% - 6px)' }} />
          </Form.Item>
        </Input.Group>
      </Form.Item>
    </Col>
  )

  return (
    <Modal
      title={modeLocal === 'add' ? 'Add New Base Product' : `Edit Base Product${details?.baseProductName ? ' - ' + details.baseProductName : ''}`}
      open={visible}
      onOk={handleOk}
      onCancel={() => onClose(false)}
      width="85vw"
      style={{ top: 4 }}
      bodyStyle={{ padding: '12px 16px', maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' }}
      maskClosable={false}
      className="compact-product-dialog"
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" className="compact-product-dialog">
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" tabBarStyle={{ marginBottom: '12px' }}>
          {/* Tab 1: General Info */}
          <Tabs.TabPane tab="General Info" key="general">
            <Row gutter={[12, 6]}>
              <Col span={11}>
                <Form.Item
                  label="Base Product Name"
                  name="baseProductName"
                  rules={[{ required: true, message: 'Please enter base product name' }]}
                >
                  <Input.TextArea size="small" rows={1} placeholder="Enter base product name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Product Code"
                  name="productCode"
                  rules={[{ required: true, message: 'Please enter product code' }]}
                >
                  <Input size="small" placeholder="Enter product code" />
                </Form.Item>
              </Col>
              <Col span={3}>
                <Form.Item label="Outsourced" name="manufactured">
                  <Radio.Group size="small" disabled>
                    <Radio value={1}>Yes</Radio>
                    <Radio value={0}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Visible to Customers" name="visible">
                  <Radio.Group size="small">
                    <Radio value={1}>Yes</Radio>
                    <Radio value={0}>No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Raw Material 1" name="rawMaterialId">
                  <Select size="small" placeholder="Select raw material" allowClear showSearch optionFilterProp="children">
                    {rawMaterialOptions.map((rm: any) => (
                      <Option key={rm.rawMaterialId} value={rm.rawMaterialId}>
                        {rm.rawMaterialName} {rm.rawMaterialCode ? `(${rm.rawMaterialCode})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Raw Material 2" name="rawMaterialId2">
                  <Select size="small" placeholder="Select raw material 2" allowClear showSearch optionFilterProp="children">
                    {rawMaterialOptions.map((rm: any) => (
                      <Option key={rm.rawMaterialId} value={rm.rawMaterialId}>
                        {rm.rawMaterialName} {rm.rawMaterialCode ? `(${rm.rawMaterialCode})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Pack Type" name="packagingTypeId">
                  <PackagingTypeCascadeSelect inlinePreview />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Base Product Note" name="description">
                  <Input.TextArea size="small" rows={2} placeholder="Note" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Quote Specs (Will show on Quote)" name="rawmaterialDesc">
                  <Input.TextArea size="small" rows={2} placeholder="Quote specs" />
                </Form.Item>
              </Col>

              {/* Sample Image (hidden)
              <Col span={24}>
                <Form.Item label="Sample Image" style={{ marginBottom: 4 }}>
                  <Upload {...uploadProps}>
                    <Button size="small" icon={<UploadOutlined />}>Upload</Button>
                  </Upload>
                </Form.Item>
              </Col>
              */}
            </Row>
          </Tabs.TabPane>

          {/* Tab 2: Specifications */}
          <Tabs.TabPane tab="Specifications" key="specifications">
            <Row gutter={[12, 4]}>
              <Col span={24}>
                <Divider orientation="left" style={{ margin: '2px 0 4px 0', fontSize: '13px' }}>Classification</Divider>
                <Form.Item name="classificationId" hidden><Input /></Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Primary Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small" style={{ width: '100%' }} placeholder="Select primary category"
                    value={classificationPrimaryId} allowClear showSearch optionFilterProp="children"
                    onChange={(value: any) => handleClassificationSelect('primary', value)}
                  >
                    {classificationChildrenOf(null).map((c: any) => (
                      <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Child Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small" style={{ width: '100%' }} placeholder="Select child category"
                    value={classificationChildId} allowClear showSearch optionFilterProp="children"
                    disabled={!classificationPrimaryId}
                    onChange={(value: any) => handleClassificationSelect('child', value)}
                  >
                    {classificationChildrenOf(classificationPrimaryId).map((c: any) => (
                      <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Grandchild Category" style={{ marginBottom: 2 }}>
                  <Select
                    size="small" style={{ width: '100%' }} placeholder="Select grandchild category"
                    value={classificationGrandchildId} allowClear showSearch optionFilterProp="children"
                    disabled={!classificationChildId}
                    onChange={(value: any) => handleClassificationSelect('grandchild', value)}
                  >
                    {classificationChildrenOf(classificationChildId).map((c: any) => (
                      <Option key={c.classificationId} value={c.classificationId}>{c.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider orientation="left" style={{ margin: '2px 0 4px 0', fontSize: '13px' }}>Packaging & Carton</Divider>
              </Col>
              <Col span={4}>
                <Form.Item label="CTN L (mm)" name="cartonLength" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="CTN W (mm)" name="cartonWidth" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="CTN H (mm)" name="cartonHeight" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="G.W. (kg)" name="grossWeigh" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="kg" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Divider orientation="left" style={{ margin: '2px 0 4px 0', fontSize: '13px' }}>Specification Details</Divider>
              </Col>
              <Col span={4}>
                <Form.Item label="Brim Fill Capacity (ml)" name="brimFillCapacityMl" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="ml" />
                </Form.Item>
              </Col>
              {sizePairItem('Top Size L x W (mm)', 'dimsTopLMm', 'dimsTopWMm')}
              {sizePairItem('Bottom Size L x W (mm)', 'dimsBaseLMm', 'dimsBaseWMm')}
              <Col span={4}>
                <Form.Item label="Height / Depth (mm)" name="productHeightMm" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Product Weight (g)" name="productWeightG" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="g" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Wall Thickness (mm)" name="wallThicknessMm" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="mm" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Place of Production" name="manufacturedLocation" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="e.g. New Zealand" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Raw Material" name="rawMaterialText" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Material" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Printing Type" name="printingType" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Printing type" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Lining/Coating" name="liningCoating" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="Type" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Factory Quality & Food Safety Certification" name="facilityQualitySystem" style={{ marginBottom: 2 }}>
                  <Input size="small" placeholder="e.g. ISO 9001, HACCP, BRC" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Environmental & Disposal" style={{ marginBottom: 2 }}>
                  <Space size="large" wrap>
                    <Form.Item name="isHomeCompostable" valuePropName="checked" noStyle>
                      <Checkbox>Home Compostable</Checkbox>
                    </Form.Item>
                    <Form.Item name="isIndustrialCompostable" valuePropName="checked" noStyle>
                      <Checkbox>Industrial Compostable</Checkbox>
                    </Form.Item>
                    <Form.Item name="isRecyclable" valuePropName="checked" noStyle>
                      <Checkbox>Recyclable</Checkbox>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Tabs.TabPane>

          {/* Tab 3: Price */}
          <Tabs.TabPane tab="Price" key="price">
            {currentBaseProductId ? (
              <div className="compact-price-tab">
                <ProductPriceManagementPage key={currentBaseProductId} baseProductId={currentBaseProductId} isEmbedded />
              </div>
            ) : (
              <Alert
                message="Save the base product first, then manage its prices here."
                type="info"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
          </Tabs.TabPane>

          {/* Tab 4: Routings */}
          <Tabs.TabPane tab="Routings" key="machine">
            {currentBaseProductId ? (
              <div className="compact-price-tab">
                <MachineActionManagementPage
                  key={currentBaseProductId}
                  baseProductId={currentBaseProductId}
                  productTypeId={details?.productTypeId}
                  isEmbedded
                />
              </div>
            ) : (
              <Alert
                message="Save the base product first, then manage machine actions here."
                type="info"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
          </Tabs.TabPane>
        </Tabs>
      </Form>
    </Modal>
  )
}

export default BaseProductManagementDialog
